/**
 * The Events page's search as URL state (Story 24, task 158).
 *
 * Like the list filters in `listSearch.ts`, the search lives in the URL so
 * it can be linked, shared and restored by Back; parsing is lenient and
 * defaults are left out. Unlike them, the default range is 7 days, not
 * All: every search is a paid Athena scan (ADR-0019 §6), and "All" here
 * means the 90-day retention.
 *
 * The preset becomes `since`/`until` when the search is sent, never in
 * the URL, so a shared "last 24 hours" link means the last 24 hours when
 * it is opened. A custom range is two UTC dates, both inclusive.
 */

import type { EventFeed, EventSearchRequest } from '@bluesphere/api-client';

export const EVENT_RANGES = ['24h', '7d', '30d', '60d', '90d', 'custom'] as const;
export type EventRange = (typeof EVENT_RANGES)[number];
export const DEFAULT_EVENT_RANGE = '7d' satisfies EventRange;

export const EVENT_RANGE_OPTIONS: Array<{ value: EventRange; label: string }> = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '60d', label: 'Last 60 days' },
  { value: '90d', label: 'Last 90 days (all retained)' },
  { value: 'custom', label: 'Custom range' },
];

const RANGE_HOURS: Record<Exclude<EventRange, 'custom'>, number> = {
  '24h': 24,
  '7d': 7 * 24,
  '30d': 30 * 24,
  '60d': 60 * 24,
  '90d': 90 * 24,
};

export const RETENTION_DAYS = 90;
const DAY_MS = 86_400_000;

/** The feeds that exist today; `m365_audit` and `generic` join when theirs do. */
export const EVENT_FEED_OPTIONS: Array<{ value: EventFeed; label: string }> = [
  { value: 'entra_signin', label: 'Sign-ins' },
  { value: 'entra_signin_noninteractive', label: 'Non-interactive sign-ins' },
  { value: 'entra_audit', label: 'Directory audits' },
];

/** The OCSF classes the feeds produce (`CLASSES` in data-access-py `events.py`). */
export const EVENT_CLASS_LABELS: Record<number, string> = {
  3001: 'Account change',
  3002: 'Authentication',
  3004: 'Entity management',
  3005: 'User access management',
  3006: 'Group management',
  6003: 'API activity',
};

export const EVENT_CLASS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any class' },
  ...Object.entries(EVENT_CLASS_LABELS).map(([value, label]) => ({ value, label })),
];

export function classLabel(classUid: number): string {
  return EVENT_CLASS_LABELS[classUid] ?? `Class ${classUid}`;
}

/**
 * The feeds a search reads when the URL names none: every feed but the
 * non-interactive sign-ins, which are ~97% of the volume (design r9 "Spike
 * results") and would bury everything else. Ticking the box adds them.
 */
export const DEFAULT_EVENT_FEEDS: EventFeed[] = EVENT_FEED_OPTIONS.map((o) => o.value).filter(
  (f) => f !== 'entra_signin_noninteractive',
);

/** Both Entra sign-in feeds: interactive and non-interactive (task 177). */
export function isSignInFeed(feed: EventFeed): boolean {
  return feed === 'entra_signin' || feed === 'entra_signin_noninteractive';
}

export type EventResult = 'success' | 'failure' | 'interrupted' | 'other' | 'unknown';

/**
 * OCSF `status_id` as a result. The Entra normalizer writes 99 for a
 * prompt inside a sign-in flow that then goes on (stay signed in, MFA,
 * device auth), so on a sign-in 99 reads "Interrupted", not failure.
 */
export function eventResult(statusId: number, feed: EventFeed): EventResult {
  if (statusId === 1) return 'success';
  if (statusId === 2) return 'failure';
  if (statusId === 99) return isSignInFeed(feed) ? 'interrupted' : 'other';
  return 'unknown';
}

export const RESULT_LABELS: Record<EventResult, string> = {
  success: 'Success',
  failure: 'Failure',
  interrupted: 'Interrupted',
  other: 'Other',
  unknown: 'Unknown',
};

/** The row's `YYYY-MM-DD HH:MM:SS.mmm` UTC stamp as ISO 8601. */
export function eventTimeIso(time: string): string {
  const iso = time.includes('T') ? time : time.replace(' ', 'T');
  return /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`;
}

export interface EventsSearch {
  range?: Exclude<EventRange, '7d'>;
  /** Custom range only: UTC dates, both inclusive. */
  from?: string;
  to?: string;
  /** Comma-separated feed names; absent is `DEFAULT_EVENT_FEEDS`. */
  feeds?: string;
  class?: number;
  user?: string;
  device?: string;
  ip?: string;
  q?: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

function text(raw: unknown, max: number): string | undefined {
  if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
  const value = String(raw).trim();
  return value ? value.slice(0, max) : undefined;
}

function date(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !DATE.test(raw)) return undefined;
  return Number.isNaN(Date.parse(`${raw}T00:00:00Z`)) ? undefined : raw;
}

function feeds(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const known = EVENT_FEED_OPTIONS.map((o) => o.value as string);
  const picked = known.filter((f) => raw.split(',').includes(f));
  // None, or exactly the default set, is the default search.
  const isDefault =
    picked.length === DEFAULT_EVENT_FEEDS.length &&
    DEFAULT_EVENT_FEEDS.every((f) => picked.includes(f));
  return picked.length === 0 || isDefault ? undefined : picked.join(',');
}

export function parseEventsSearch(search: Record<string, unknown>): EventsSearch {
  const range = EVENT_RANGES.find((r) => r === search.range);
  const custom = range === 'custom';
  const classUid = Number(search.class);
  return {
    range: range && range !== DEFAULT_EVENT_RANGE ? (range as EventsSearch['range']) : undefined,
    from: custom ? date(search.from) : undefined,
    to: custom ? date(search.to) : undefined,
    feeds: feeds(search.feeds),
    class: classUid in EVENT_CLASS_LABELS ? classUid : undefined,
    // The API's own bounds (MAX_IDENTIFIER, MAX_TEXT); it validates again.
    user: text(search.user, 256),
    device: text(search.device, 256),
    ip: text(search.ip, 64),
    q: text(search.q, 128),
  };
}

/** A UTC date `days` from `now`'s date, as `YYYY-MM-DD`. */
export function utcDate(now: Date, days = 0): string {
  return new Date(now.getTime() + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The request body for the search in the URL, with the range resolved
 * against `now`. A custom range missing either end falls back to the
 * default; the API rejects one that is backwards or too long, naming the
 * field, and the page shows that.
 */
export function requestFor(search: EventsSearch, now: Date = new Date()): EventSearchRequest {
  const range = search.range ?? DEFAULT_EVENT_RANGE;
  let since: string;
  let until: string;
  if (range === 'custom' && search.from && search.to) {
    since = `${search.from}T00:00:00.000Z`;
    const end = new Date(Date.parse(`${search.to}T00:00:00Z`) + DAY_MS);
    until = (end < now ? end : now).toISOString();
  } else {
    const hours = RANGE_HOURS[range === 'custom' ? DEFAULT_EVENT_RANGE : range];
    since = new Date(now.getTime() - hours * 3_600_000).toISOString();
    until = now.toISOString();
  }
  const request: EventSearchRequest = { since, until };
  request.feeds = search.feeds ? (search.feeds.split(',') as EventFeed[]) : DEFAULT_EVENT_FEEDS;
  if (search.class) request.classes = [search.class];
  if (search.user) request.user = search.user;
  if (search.device) request.device = search.device;
  if (search.ip) request.ip = search.ip;
  if (search.q) request.q = search.q;
  return request;
}

/** Whether anything narrows the search beyond the default range. */
export function isFiltered(search: EventsSearch): boolean {
  return Object.values(search).some((v) => v !== undefined);
}
